import Api from 'services/api';
import ApiFiles from 'services/api_files';

// Contrato do Dr. Licita v3 (chat conversacional) via BFF Rails:
// o histórico e o rascunho vivem no backend; o front envia apenas
// { chat_id, content } e renderiza os payloads estruturados.

export interface TurnoAtendimento {
  em_andamento: boolean;
  parando: boolean;
}

export interface DrLicitaChatSummary {
  id: number;
  mode: string;
  title: string | null;
  document_id: number | null;
  project_id?: number | null;
  last_message_at: string | null;
  // Modo Atendimento: turno ainda rodando no agente (a tela recarregou ou
  // trocou de conversa no meio da resposta)
  turno?: TurnoAtendimento;
}

// Resultado das edições pedidas pelo chat (BUSCAR/TROCAR) no painel
export interface CanvasEdicoes {
  aplicadas: number;
  nao_aplicadas: number;
  ambiguas?: number;
  sobras?: Array<{ trecho: string; vezes: number }>;
}

export interface DraftFundamentoLiteral {
  tese_id: number;
  trecho: string;
}

export interface DraftSecao {
  titulo: string;
  texto: string;
  teses_ids: number[];
  fundamentos_literais?: DraftFundamentoLiteral[];
}

export interface DraftPayload {
  rascunho: { titulo_peca: string; secoes: DraftSecao[] };
  verificacao: {
    aprovado: boolean;
    citacoes_no_rascunho: string[];
    nao_encontradas_nas_fontes: string[];
    fundamentos_nao_literais: unknown[];
  };
  teses_utilizadas: Array<{ id: number; titulo: string }>;
}

export interface OpcoesPayload {
  pergunta: string | null;
  opcoes: string[];
}

export interface PecaSalvaPayload {
  id: number;
  doc_nr: string;
  titulo: string;
  atualizada?: boolean;
}

// Modo Atendimento: documento do painel (Canvas) escrito ao vivo pelo agente
export interface CanvasResumo {
  title: string;
  version: number;
  document_type?: string;
}

export interface CanvasDocument extends CanvasResumo {
  content: string;
  updated_by?: 'assistant' | 'user';
  dirty_for_model?: boolean;
  meta?: { tipo_peca?: string; lei?: string };
  updated_at?: string;
}

export interface DrLicitaChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  payload?: {
    opcoes?: OpcoesPayload;
    peca_salva?: PecaSalvaPayload;
    canvas?: CanvasResumo;
    canvas_edits?: CanvasEdicoes;
    // Modo Atendimento: resposta de erro gravada pelo agente / turno parado
    falha?: boolean;
    erro?: string;
    interrompida?: boolean;
  } | null;
  rating: number | null;
  created_at: string;
  // Só na tela: texto a reenviar pelo "Tentar novamente" de uma falha
  retry?: string;
}

export interface SendMessageResponse {
  content: string;
  draft?: DraftPayload;
  opcoes?: OpcoesPayload;
  peca_salva?: PecaSalvaPayload;
  assistant_message_id?: number;
  canvas?: CanvasResumo;
  canvas_conflict?: boolean;
  canvas_edits?: CanvasEdicoes;
  interrompida?: boolean;
}

export interface KickoffPayload {
  content: string | null;
  opcoes?: OpcoesPayload | null;
  assistant_message_id?: number;
}

export const createDrLicitaChat = async (
  mode: string,
  kickoff?: boolean,
  tipoPeca?: string,
): Promise<{ chat: DrLicitaChatSummary; kickoff?: KickoffPayload | null } | null> => {
  const response = await Api.post('/dr_licita/chats', {
    mode,
    kickoff: Boolean(kickoff),
    tipo_peca: tipoPeca,
  });
  if (response?.status === 201) return response.data;
  return null;
};

// Apaga a conversa do histórico (a peça salva vinculada não é afetada)
export const deleteDrLicitaChat = async (chatId: number): Promise<boolean> => {
  const response = await Api.delete(`/dr_licita/chats/${chatId}`);
  return response?.status === 200;
};

export const renameDrLicitaChat = async (chatId: number, title: string): Promise<boolean> => {
  const response = await Api.put(`/dr_licita/chats/${chatId}/titulo`, { title });
  return response?.status === 200;
};

// Título de exibição do histórico: caixa normal no lugar do CAPS dos títulos
export const tituloConversa = (chat: DrLicitaChatSummary): string => {
  const titulo = (chat.title || '').trim();
  // Sem título ainda (ele chega com a primeira resposta): sem o número interno
  if (!titulo) return 'Nova conversa';
  const minusculas = titulo.toLocaleLowerCase('pt-BR');
  return minusculas.charAt(0).toLocaleUpperCase('pt-BR') + minusculas.slice(1);
};

export const fetchDrLicitaChats = async (): Promise<DrLicitaChatSummary[]> => {
  const response = await Api.get('/dr_licita/chats');
  if (response?.status === 200) return response.data.chats || [];
  return [];
};

export const fetchDrLicitaChatMessages = async (
  chatId: number,
): Promise<{
  chat: DrLicitaChatSummary & { current_draft?: DraftPayload };
  messages: DrLicitaChatMessage[];
} | null> => {
  const response = await Api.get(`/dr_licita/chat_messages?chat_id=${chatId}`);
  if (response?.status === 200) return response.data;
  return null;
};

export const sendDrLicitaChatMessage = async (
  chatId: number,
  content: string,
): Promise<SendMessageResponse | null> => {
  const response = await Api.post('/dr_licita/chat_messages', { chat_id: chatId, content });
  if (response?.status === 201) return response.data;
  return null;
};

export interface StreamEvent {
  type:
    | 'tool_start'
    | 'tool_progress'
    | 'tool_end'
    | 'delta'
    | 'done'
    | 'error'
    // Modo Atendimento: atividade e painel da petição
    | 'reasoning'
    | 'web_search'
    | 'document_start'
    | 'document_delta'
    | 'document_end'
    | 'document_updating'
    | 'document_updated';
  tool?: string;
  summary?: string;
  content?: string;
  result?: SendMessageResponse;
  error?: string;
  title?: string;
  document_type?: string;
  document?: CanvasDocument;
  edits?: CanvasEdicoes;
  // Erro do modo Atendimento: classe (turno_em_andamento, limite, tempo...)
  code?: string;
  assistant_message_id?: number;
}

// Streaming SSE via fetch (EventSource não suporta POST). Devolve o result
// do evento done, ou null em qualquer falha — o chamador usa o caminho
// não-stream como fallback.
export const streamDrLicitaChatMessage = async (
  chatId: number,
  content: string,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<SendMessageResponse | null> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/dr_licita/chat_streams`, {
      method: 'POST',
      signal,
      credentials: 'include',
      // Accept json: sem ele o Rails trata como HTML e o CSRF derruba a
      // requisição com 422 — o stream caía no fallback silenciosamente
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        frontend: 'true',
      },
      body: JSON.stringify({ chat_id: chatId, content }),
    });
    if (!response.ok || !response.body) return null;

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let done: SendMessageResponse | null = null;
    let failed = false;

    const handleFrame = (frame: string) => {
      const line = frame.split('\n').find((l) => l.startsWith('data: '));
      if (!line) return;
      try {
        const event: StreamEvent = JSON.parse(line.slice(6));
        onEvent(event);
        if (event.type === 'done' && event.result) done = event.result;
        if (event.type === 'error') failed = true;
      } catch {
        // frame malformado: ignora e segue o stream
      }
    };

    let finished = false;
    while (!finished && !failed) {
      // Leitura sequencial de stream exige await em loop
      // eslint-disable-next-line no-await-in-loop
      const chunk = await reader.read();
      finished = chunk.done;
      if (chunk.value) {
        buffer += decoder.decode(chunk.value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        frames.forEach(handleFrame);
      }
    }
    return failed ? null : done;
  } catch {
    return null;
  }
};

// Avaliação 👍/👎 de uma resposta do assistente (null limpa)
export const rateDrLicitaChatMessage = async (
  messageId: number,
  rating: 1 | -1 | null,
): Promise<boolean> => {
  const response = await Api.post('/dr_licita/chat_ratings', { message_id: messageId, rating });
  return response?.status === 201;
};

// O que a leitura do anexo conseguiu: o texto guardado tem teto (o edital
// longo é lido só até certa página)
export interface DocumentoAnexado {
  nome: string;
  caracteres: number;
  truncado?: boolean;
  paginas_total?: number;
  pagina_corte?: number;
  paginas_sem_texto?: number;
}

export const uploadDrLicitaChatAttachment = async (
  chatId: number,
  file: File,
): Promise<{ documento?: DocumentoAnexado; error?: string }> => {
  const formData = new FormData();
  formData.append('chat_id', String(chatId));
  formData.append('file', file);
  const response = await Api.post('/dr_licita/chat_attachments', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (response?.status === 201) return response.data;
  return { error: response?.data?.error || 'Falha ao enviar o arquivo' };
};

// --- Modo Atendimento: painel da petição (Canvas) ---

export const fetchCanvas = async (chatId: number): Promise<CanvasDocument | null> => {
  const response = await Api.get(`/dr_licita/chats/${chatId}/canvas`);
  if (response?.status === 200) return response.data.document || null;
  return null;
};

export type SaveCanvasResult =
  | { ok: true; document: CanvasDocument }
  | { ok: false; conflict: CanvasDocument }
  | { ok: false; error: string };

// PUT com a versão que o painel viu: 409 devolve o documento atual (o agente
// escreveu no meio) e o painel decide sem perder o texto do usuário
export const saveCanvas = async (
  chatId: number,
  content: string,
  expectedVersion: number,
  title?: string,
): Promise<SaveCanvasResult> => {
  const response = await Api.put(`/dr_licita/chats/${chatId}/canvas`, {
    content,
    expected_version: expectedVersion,
    title,
  });
  if (response?.status === 200) return { ok: true, document: response.data.document };
  if (response?.status === 409 && response.data?.current) {
    return { ok: false, conflict: response.data.current };
  }
  return { ok: false, error: response?.data?.detail?.error || 'Não foi possível salvar' };
};

export const downloadCanvasDocx = async (chatId: number, title: string): Promise<boolean> => {
  const response = await ApiFiles.get(`/dr_licita/chats/${chatId}/canvas/docx`);
  if (response?.status !== 200) return false;
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${title || 'Peticao'}.docx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return true;
};

export interface CatalogoAtendimento {
  tipos: Array<{ id: number; nome: string }>;
  leis: Array<{ id: number; nome: string }>;
}

export const fetchAtendimentoCatalogo = async (): Promise<CatalogoAtendimento | null> => {
  const response = await Api.get('/dr_licita/atendimento/catalogo');
  if (response?.status === 200) return response.data;
  return null;
};

export type SalvarPeticaoResult =
  | { ok: true; peticao: PecaSalvaPayload }
  // O painel não trouxe o tipo ou a lei: o usuário escolhe e reenvia
  | { ok: false; precisa: { document_type_id: number | null; law_type_id: number | null } }
  | { ok: false; error: string };

export const saveCanvasPeticao = async (
  chatId: number,
  ids?: { document_type_id?: number; law_type_id?: number },
): Promise<SalvarPeticaoResult> => {
  const response = await Api.post(`/dr_licita/chats/${chatId}/canvas/peticao`, ids || {});
  if (response?.status === 200) return { ok: true, peticao: response.data.peticao };
  if (response?.status === 422 && response.data?.error === 'tipo_ou_lei_ausente') {
    return {
      ok: false,
      precisa: {
        document_type_id: response.data.document_type_id ?? null,
        law_type_id: response.data.law_type_id ?? null,
      },
    };
  }
  return {
    ok: false,
    error: response?.data?.detail?.error || 'Não foi possível salvar em Petições',
  };
};

// --- Modo Atendimento: turno em andamento e botão Parar ---

export const fetchTurnoAtendimento = async (chatId: number): Promise<TurnoAtendimento | null> => {
  const response = await Api.get(`/dr_licita/chats/${chatId}/turno`);
  if (response?.status === 200) return response.data;
  return null;
};

// O agente grava o que já saiu e encerra o turno no pulso seguinte (~2 s)
export const pararTurnoAtendimento = async (chatId: number): Promise<boolean> => {
  const response = await Api.post(`/dr_licita/chats/${chatId}/parar`, {});
  return response?.status === 200 && Boolean(response.data?.parando);
};
